import { useEffect, useMemo, useState } from 'react';
import {
  ChevronDown, ChevronRight, Folder, ImageIcon, FileVideo, FileText,
  Loader2, CheckCircle2, AlertCircle, Minus, X, RefreshCw, UploadCloud,
} from 'lucide-react';

/**
 * DJI FlightHub 2-style floating upload panel with folder tree visualization.
 * Fixed bottom-right, minimizable (Google Drive style), survives folder navigation.
 */

const formatSize = (bytes) => {
  if (!bytes || bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const STATUS_ICON_MAP = {
  done: { icon: CheckCircle2, cls: 'text-green-600' },
  error: { icon: AlertCircle, cls: 'text-red-600' },
  retrying: { icon: RefreshCw, cls: 'text-amber-600 animate-spin' },
  compressing: { icon: Loader2, cls: 'text-cyan-600 animate-spin' },
  uploading: { icon: Loader2, cls: 'text-gold animate-spin' },
  pending: { icon: Loader2, cls: 'text-gray-400' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_ICON_MAP[status] || STATUS_ICON_MAP.pending;
  const Icon = cfg.icon;
  return <Icon size={12} className={cfg.cls} />;
};

const FileTypeIcon = ({ type }) => {
  if (type === 'photo') return <ImageIcon size={12} className="text-blue-500" />;
  if (type === 'video') return <FileVideo size={12} className="text-purple-500" />;
  return <FileText size={12} className="text-amber-600" />;
};

/**
 * Flatten the upload queue into an ordered list of rows.
 * Each row is either { kind: 'folder', ... } or { kind: 'file', ... } with
 * a precomputed `depth` so the UI can render indentation without recursion.
 */
const buildFlatRows = (items, expandedPaths) => {
  // 1) Build a tree structure of folders + files
  const root = { name: '', path: '', children: new Map(), files: [] };
  for (let idx = 0; idx < items.length; idx++) {
    const it = items[idx];
    const dirPath = it._dirPath || '';
    const segs = dirPath ? dirPath.split('/') : [];
    let node = root;
    for (const seg of segs) {
      if (!node.children.has(seg)) {
        node.children.set(seg, {
          name: seg,
          path: node.path ? `${node.path}/${seg}` : seg,
          children: new Map(),
          files: [],
        });
      }
      node = node.children.get(seg);
    }
    node.files.push({ ...it, _idx: idx });
  }

  // 2) Compute aggregated stats per folder (bottom-up via iterative post-order)
  const stack = [{ node: root, parent: null, visited: false }];
  const postOrder = [];
  while (stack.length) {
    const frame = stack.pop();
    if (frame.visited) {
      postOrder.push(frame.node);
    } else {
      stack.push({ ...frame, visited: true });
      for (const child of frame.node.children.values()) {
        stack.push({ node: child, parent: frame.node, visited: false });
      }
    }
  }
  for (const node of postOrder) {
    let total = 0;
    let done = 0;
    let error = 0;
    let totalBytes = 0;
    let uploadedBytes = 0;
    for (const f of node.files) {
      total++;
      totalBytes += f.size || 0;
      uploadedBytes += (f.size || 0) * (f.progress || 0) / 100;
      if (f.status === 'done') done++;
      if (f.status === 'error') error++;
    }
    for (const child of node.children.values()) {
      total += child.agg.total;
      done += child.agg.done;
      error += child.agg.error;
      totalBytes += child.agg.totalBytes;
      uploadedBytes += child.agg.uploadedBytes;
    }
    node.agg = {
      total, done, error, totalBytes, uploadedBytes,
      pct: totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0,
    };
  }

  // 3) Pre-order traversal to flatten, respecting expandedPaths
  const rows = [];
  let folderCount = 0;

  const linearize = [{ node: root, depth: -1, first: true }];
  while (linearize.length) {
    const frame = linearize.pop();
    if (frame._file) {
      rows.push({ kind: 'file', depth: frame.depth, item: frame._file });
      continue;
    }
    const { node, depth } = frame;
    if (frame.first) {
      if (node.name) {
        folderCount++;
        rows.push({
          kind: 'folder',
          name: node.name,
          path: node.path,
          depth,
          agg: node.agg,
        });
        if (!expandedPaths.has(node.path)) continue;
      }
      // Enqueue files AFTER children so children appear first.
      // Since LIFO: push files first (they pop last).
      for (let i = node.files.length - 1; i >= 0; i--) {
        linearize.push({ _file: node.files[i], depth: depth + 1 });
      }
      const children = Array.from(node.children.values()).sort((a, b) => a.name.localeCompare(b.name));
      for (let i = children.length - 1; i >= 0; i--) {
        linearize.push({ node: children[i], depth: depth + 1, first: true });
      }
    }
  }

  return { rows, folderCount, rootAgg: root.agg };
};

const FolderRow = ({ row, expanded, onToggle }) => {
  const { total, done, pct, error } = row.agg;
  const hasAny = total > 0;
  return (
    <button
      type="button"
      onClick={() => onToggle(row.path)}
      className="w-full flex items-center gap-1.5 py-1 px-1 rounded hover:bg-gray-100 group"
      style={{ paddingLeft: 4 + row.depth * 12 }}
      data-testid={`tree-folder-${row.path.replace(/\//g, '__')}`}
    >
      {hasAny
        ? (expanded
            ? <ChevronDown size={11} className="text-gray-500 flex-shrink-0" />
            : <ChevronRight size={11} className="text-gray-500 flex-shrink-0" />)
        : <span className="w-[11px] flex-shrink-0" />}
      <Folder size={12} className="text-gold flex-shrink-0" />
      <span className="text-navy text-[11px] font-medium truncate flex-1 text-left">{row.name}</span>
      <span className="text-[9px] font-mono text-gray-500 flex-shrink-0">
        {done}/{total}
      </span>
      <div className="flex-shrink-0 w-10 h-[3px] bg-gray-200 rounded overflow-hidden">
        <div
          className={`h-full transition-all ${error > 0 ? 'bg-red-500' : 'bg-green-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[9px] font-mono text-gray-400 flex-shrink-0 w-10 text-right">{pct}%</span>
    </button>
  );
};

const FileRow = ({ row, onRestart }) => {
  const item = row.item;
  return (
    <div
      className={`flex items-center gap-2 py-1 px-2 rounded hover:bg-gray-50 ${item.status === 'error' ? 'bg-red-50/60' : ''}`}
      style={{ paddingLeft: 8 + row.depth * 12 }}
      data-testid={`tree-upload-item-${item.name}`}
    >
      <FileTypeIcon type={item.type} />
      <div className="flex-1 min-w-0">
        <p className="text-navy text-[11px] truncate" title={item.name}>{item.name}</p>
        {item.status === 'uploading' && item.progress > 0 && (
          <div className="w-full h-[2px] bg-gray-200 rounded overflow-hidden mt-0.5">
            <div className="h-full bg-gold transition-all" style={{ width: `${item.progress}%` }} />
          </div>
        )}
        {item._retryInfo && item.status === 'retrying' && (
          <p className="text-[9px] font-mono text-amber-600 mt-0.5 truncate">{item._retryInfo}</p>
        )}
        {item._errorMessage && item.status === 'error' && (
          <p className="text-[9px] font-mono text-red-600 mt-0.5 truncate" title={item._errorMessage}>
            {item._errorMessage}
          </p>
        )}
        {item._compressedRatio && item.status !== 'compressing' && (
          <p className="text-[9px] font-mono text-cyan-600 mt-0.5 truncate">
            -{item._compressedRatio}% ({formatSize(item._compressedFrom)} → {formatSize(item.size)})
          </p>
        )}
      </div>
      <span className="text-[9px] font-mono text-gray-400 flex-shrink-0 w-14 text-right">
        {item.status === 'uploading' && item.speed > 0
          ? `${formatSize(item.speed)}/s`
          : formatSize(item.size)}
      </span>
      <div className="flex-shrink-0 w-4 flex items-center justify-center">
        <StatusBadge status={item.status} />
      </div>
      {item.status === 'error' && onRestart && (
        <button
          onClick={() => onRestart(item._idx)}
          className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 bg-gold/10 hover:bg-gold/20 border border-gold/40 rounded text-gold text-[9px] font-bold uppercase tracking-wider transition-colors"
          title="Reiniciar upload"
          data-testid={`tree-restart-${item.name}`}
        >
          <RefreshCw size={9} />
        </button>
      )}
    </div>
  );
};

const UploadTreePanel = ({ uploadQueue, isUploading, onRestart, onRestartAll, onClose }) => {
  const [minimized, setMinimized] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState(() => new Set(['']));

  // Auto-expand folder paths whenever the queue grows
  useEffect(() => {
    const paths = new Set(['']);
    for (const it of uploadQueue) {
      const d = it._dirPath || '';
      if (!d) continue;
      const segs = d.split('/');
      let cum = '';
      for (const s of segs) {
        cum = cum ? `${cum}/${s}` : s;
        paths.add(cum);
      }
    }
    setExpandedPaths((prev) => {
      const merged = new Set(prev);
      paths.forEach((p) => merged.add(p));
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadQueue.length]);

  const togglePath = (p) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const { rows, folderCount, rootAgg } = useMemo(
    () => buildFlatRows(uploadQueue, expandedPaths),
    [uploadQueue, expandedPaths],
  );

  const { total = 0, done = 0, error = 0, pct = 0, totalBytes = 0, uploadedBytes = 0 } = rootAgg || {};

  const activeItem = uploadQueue.find((q) => q.speed > 0 && q.status === 'uploading');
  const currentSpeed = activeItem?.speed || 0;
  const remaining = totalBytes - uploadedBytes;
  const eta = currentSpeed > 0 ? Math.ceil(remaining / currentSpeed) : 0;
  const etaStr = eta > 60 ? `${Math.floor(eta / 60)}m ${eta % 60}s` : eta > 0 ? `${eta}s` : '';
  const allDone = !isUploading && total > 0 && done === total;
  const hasErrors = error > 0;

  if (uploadQueue.length === 0) return null;

  let headerLabel = 'Fila de upload';
  if (allDone) headerLabel = 'Upload concluído';
  else if (hasErrors && !isUploading) headerLabel = 'Upload com falhas';
  else if (isUploading) headerLabel = `Enviando ${total} ${total > 1 ? 'arquivos' : 'arquivo'}`;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[380px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col"
      style={{ boxShadow: '0 20px 50px -12px rgba(10, 22, 40, 0.35)' }}
      data-testid="upload-tree-panel"
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 cursor-pointer select-none"
        style={{ background: 'linear-gradient(135deg, #0A1628 0%, #1E3A5F 100%)' }}
        onClick={() => setMinimized((m) => !m)}
        data-testid="upload-tree-header"
      >
        <div className="relative flex-shrink-0">
          {isUploading && <Loader2 size={16} className="text-gold animate-spin" />}
          {!isUploading && allDone && <CheckCircle2 size={16} className="text-green-400" />}
          {!isUploading && !allDone && hasErrors && <AlertCircle size={16} className="text-red-400" />}
          {!isUploading && !allDone && !hasErrors && <UploadCloud size={16} className="text-gold" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-xs font-rajdhani font-semibold uppercase tracking-wider truncate">
            {headerLabel}
          </p>
          <p className="text-gray-300 text-[9px] font-mono mt-0.5 truncate">
            {done}/{total} concluídos
            {folderCount > 0 ? ` · ${folderCount} pasta(s)` : ''}
            {' · '}{formatSize(uploadedBytes)}/{formatSize(totalBytes)}
            {currentSpeed > 0 ? ` · ${formatSize(currentSpeed)}/s` : ''}
            {etaStr ? ` · ETA ${etaStr}` : ''}
          </p>
        </div>
        <button
          type="button"
          className="p-1 text-gray-300 hover:text-white hover:bg-white/10 rounded transition-colors"
          onClick={(e) => { e.stopPropagation(); setMinimized((m) => !m); }}
          title={minimized ? 'Expandir' : 'Minimizar'}
          data-testid="upload-tree-minimize"
        >
          {minimized ? <ChevronRight size={14} style={{ transform: 'rotate(-90deg)' }} /> : <Minus size={14} />}
        </button>
        {!isUploading && onClose && (
          <button
            type="button"
            className="p-1 text-gray-300 hover:text-white hover:bg-white/10 rounded transition-colors"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            title="Fechar"
            data-testid="upload-tree-close"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Global progress bar */}
      <div className="h-[3px] bg-gray-100 w-full">
        <div
          className={`h-full transition-all ${hasErrors ? 'bg-red-500' : allDone ? 'bg-green-500' : 'bg-gold'}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {!minimized && (
        <div className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto max-h-[50vh] px-2 py-2" data-testid="upload-tree-body">
            {rows.map((row, idx) => (
              row.kind === 'folder'
                ? <FolderRow key={`f-${row.path}`} row={row} expanded={expandedPaths.has(row.path)} onToggle={togglePath} />
                : <FileRow key={`x-${row.item._idx}`} row={row} onRestart={onRestart} />
            ))}
          </div>

          {hasErrors && (
            <div className="border-t border-red-200 px-3 py-2 flex items-center justify-between gap-2 bg-red-50/60">
              <span className="text-[10px] font-mono text-red-600">
                {error} falha{error > 1 ? 's' : ''}
              </span>
              <button
                type="button"
                onClick={onRestartAll}
                disabled={isUploading}
                className="flex items-center gap-1 px-2 py-1 bg-gold/10 hover:bg-gold/20 border border-gold/40 rounded text-gold text-[10px] font-bold uppercase tracking-wider disabled:opacity-50 transition-colors"
                data-testid="upload-tree-restart-all"
              >
                <RefreshCw size={11} /> REINICIAR TODOS
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UploadTreePanel;
